import BaseModel from './BaseModel'
import Blackout from './Blackout'
import Facility from './Facility'
import LocationHours from './LocationHours'
import LocationProduct from './LocationProduct'
import LocationSolution from './LocationSolution'
import Organization from './Organization'
import Parcs from './Parcs'
import UserSolution from './UserSolution'
import PermissionsService from '../services/PermissionsService'
import handleAxiosError from '../utils/handleAxiosError'
import type { UserSolutionType } from '../types/misc'
import type { VSelectItem } from '../types/vuetify'

/**
 * @todo Add types for API methods.
 */
export default class Location extends BaseModel {

  static entity = 'locations'
  static primaryKey = 'ID'

  declare ID: number
  declare organizationID: number
  declare locationName: string
  declare address: string
  declare facilityID: number|null
  declare timezone: string
  declare totalStalls: number
  declare onlineStalls: number
  declare gatedBarrier: boolean
  declare live: boolean
  declare status: string
  declare internalNotes: null|string
  declare blackouts: Blackout[]
  declare facility: Facility
  declare locationProducts: LocationProduct[]
  declare locationSolution: LocationSolution
  declare organization: Organization
  declare parcs: Parcs[]
  declare userSolutions: UserSolution[]
  declare usersWithSolutions: number

  static fields() {
    return {
      
      // fields
      ID: this.number(null),
      organizationID: this.number(null),
      locationName: this.string(''),
      address: this.string(''),
      facilityID: this.number(null).nullable(),
      timezone: this.string(''),
      totalStalls: this.number(0),
      onlineStalls: this.number(0),
      gatedBarrier: this.boolean(false),
      live: this.boolean(false),
      status: this.string('onboarded'),
      internalNotes: this.string('').nullable(),
      usersWithSolutions: this.number(null), // virtual column

      // relationships
      blackouts: this.hasMany(Blackout, 'locationID'),
      facility: this.belongsTo(Facility, 'facilityID'),
      locationHours: this.hasMany(LocationHours, 'locationID'),
      locationProducts: this.hasMany(LocationProduct, 'locationID'),
      locationSolution: this.hasOne(LocationSolution, 'locationID'),
      organization: this.belongsTo(Organization, 'organizationID'),
      parcs: this.hasMany(Parcs, 'locationID'),
      userSolutions: this.hasMany(UserSolution, 'locationID'),

    }
  }

  getDisplayName(): string {
    let name = this.locationName
    if (this.status === 'onboarded') {
      name += ' (onboarded)'
    } else if (this.status === 'archived') {
      name += ' (archived)'
    }

    return name
  }

  /**
   * Returns locations matching the arguments as an array of objects, suitable
   * for use in the :items prop of Vuetify form components.
   * 
   * solution: If provided, only locations that have the given location solution will be included.
   * permission: Either 'read' or 'write'. If provided, only locations to which the user has the specified user solution will be included.
   * organizationID: If provided, only locations belonging to the specified organization will be included.
   */
  static getOptions(
    solution: null | UserSolutionType = null,
    permission: null | 'read' | 'write' = null,
    organizationID: null | number = null,
    liveOnly: boolean = false,
  ): VSelectItem[] {

    const options: {title: string, value: number}[] = []

    if (solution === null && permission !== null) {
      throw 'permission parameter doesn\'t make sense without solution parameter'
    }

    let query = this.query().orderBy((location) => {
      // @ts-ignore
      return location.getDisplayName().toUpperCase()
    })

    if (organizationID !== null) {
      query.where('organizationID', organizationID)
    }

    if (liveOnly) {
      query.where('live', true)
    }

    if (solution) {
      query.whereHas('locationSolution', (query) => {
        query.where(solution, true)
      })
    }

    let locations = query.get()

    if (solution) {
      locations = locations.filter((location) => {
        return PermissionsService.can(solution + '.' + permission, location.ID)
      })
    }

    locations.forEach((location) => {
      options.push({
        title: location.getDisplayName(),
        value: location.ID,
      })
    })

    return options

  }

  /**
   * API integrations
   */

  static fetchAll() {
    return this.api()
      .get('/locations/get_all', {
        dataTransformer: ({ data }) => {

          // This endpoint also returns LocationSolutions.
          data.locationSolutions.forEach((locationSolution: any) => {
            delete locationSolution.locationName
            LocationSolution.insert({
              data: locationSolution,
            })
          })

          // ...and LocationProducts!
          data.locationProducts.forEach((locationProduct: any) => {
            locationProduct.channelFacilityIDs = locationProduct.channelFacilities
            delete locationProduct.channelFacilities
            LocationProduct.insert({ data: locationProduct })
          })

          // ...and LocationHours!
          data.locationHours.forEach((locationHours: any) => {
            LocationHours.insert({ data: locationHours })
          })

          return data.locations

        },
      })
      .catch(handleAxiosError)
  }

  static saveNew(payload: any) {
    const massagedPayload = Location._massagePayloadForUpsert(payload)
    return this.api()
      .post('/locations/create', massagedPayload)
      .catch(handleAxiosError)
  }

  static saveExisting(payload: any) {
    const massagedPayload = Location._massagePayloadForUpsert(payload)
    return this.api()
      .put('/locations/location/' + massagedPayload.ID, massagedPayload)
      .then((result) => {
        const deletedLocationHourIds = massagedPayload?.locationHours?.delete ?? []
        deletedLocationHourIds.forEach((id: number) => {
          LocationHours.delete(id)
        })
        return result
      })
      .catch(handleAxiosError)
  }

  static _massagePayloadForUpsert(payload: any): any {

    // Temporary hack to work around a bug in the API:
    // https://app.asana.com/0/1202228339672381/1202254059254488
    // Feel free to remove this once the bug has been fixed.

    const massagedPayload = { ...payload }
    massagedPayload.name = massagedPayload.locationName
    return massagedPayload

  }

}
